import { BRAND_STROKE_WIDTH } from '@mentimeter/global-variables';
import {
  scaleBand,
  scaleLinear,
  type ScaleBand,
  type ScaleLinear,
} from 'd3-scale';
import { max } from 'd3-array';
import { d3, wordWrap, shouldUpdateText } from './d3';
import { rangeBandOptions, NORMAL_FONT_SIZE } from './utils';

const LAYOUT = {
  width: 960,
  height: 540,
};
const MARGIN = {
  top: 0,
  right: LAYOUT.width * 0.1,
  bottom: LAYOUT.height * 0.05,
  left: LAYOUT.width * 0.1,
};
const WIDTH = LAYOUT.width - MARGIN.right - MARGIN.left;
const HEIGHT = LAYOUT.height - MARGIN.top - MARGIN.bottom;
const Y_AXIS_PADDING = 12;
const ANIMATION_DURATION = 750;
const ANIMATION_DELAY = ANIMATION_DURATION / 4;
const BAR_HEIGHT_RANGE_BAND_RATIO = 0.35;
const BAR_TEXT_PADDING = 10;
const BAR_MIN_WIDTH = 3;

export interface PrioritisationResultT {
  id: number;
  label: string;
  score: number;
}

interface Data extends PrioritisationResultT {
  index: number;
}
interface T {
  selector: string;
  data: Data[];
  animationMultiplier: 0 | 1;
  lineColor: string;
  textColor: string;
  fillColors: string[];
  isScoreHidden: boolean;
}

/**
 * 100pts: animated visualization that prioritizes items by total points
 */
export class Prioritisation implements T {
  readonly selector: string;
  readonly data: Data[];
  readonly animationMultiplier: 1 | 0;
  readonly lineColor: string;
  readonly textColor: string;
  readonly fillColors: string[];
  readonly x: ScaleLinear<number, number> = scaleLinear();
  readonly y: ScaleBand<number> = scaleBand();
  readonly isScoreHidden: boolean = false;

  /**
   * @param selector: string selection to a dom node, e.g., #prioritisation-${questionId}
   * @param data: result data that can be sorted by score
   * @param lineColor: color string for axis line, e.g., #000
   * @param textColor: color string, e.g., #e8e8e8
   * @param fillColors: array of colors for bars, e.g., ['green', 'yellow', 'blue']
   * @param animationMultiplier: value to multiply animation durations by, 1 (enabled) or 0 (disabled)
   * @param isScoreHidden: boolean to hide the score text
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.data = props.data;
    this.animationMultiplier = props.animationMultiplier;
    this.lineColor = props.lineColor;
    this.textColor = props.textColor;
    this.fillColors = props.fillColors;
    this.isScoreHidden = props.isScoreHidden;
  }

  private updateLine = (): void => {
    d3.select(this.selector)
      .selectAll('line')
      .data(['']) // Length one to append no more than one element
      .join('line')
      .attr('x1', -BRAND_STROKE_WIDTH / 2)
      .attr('x2', -BRAND_STROKE_WIDTH / 2)
      .attr('y1', 0)
      .attr('y2', HEIGHT)
      .attr('stroke-width', BRAND_STROKE_WIDTH)
      .attr('stroke', this.lineColor);
  };

  private updateAxis = (): Promise<void[]> => {
    // Select
    const text = d3
      .select(this.selector)
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`)
      .selectAll<SVGTextElement, Data>('.axis-text')
      .data(this.data, (d) => d.id);
    // Enter
    const textEnter = text
      .enter()
      .append('text')
      .text(this.isScoreHidden ? '' : '0')
      .attr('class', 'axis-text')
      .attr(
        'transform',
        (d) =>
          `translate(${-Y_AXIS_PADDING}, ${
            (this.y(d.id) ?? 0) + this.y.bandwidth() - BAR_TEXT_PADDING
          })`,
      )
      .attr('text-anchor', 'end')
      .attr('font-weight', 600)
      .attr('fill', this.textColor);
    // Update
    const axisTransition = text
      .merge(textEnter)
      .attr('font-size', NORMAL_FONT_SIZE)
      .text((d) => (this.isScoreHidden ? '' : `${Math.round(d.score)}`))
      .transition()
      .delay(ANIMATION_DELAY * this.animationMultiplier)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr(
        'transform',
        (d) =>
          `translate(${-Y_AXIS_PADDING}, ${
            (this.y(d.id) ?? 0) + this.y.bandwidth() - BAR_TEXT_PADDING
          })`,
      )
      .attr('fill', this.textColor)
      .end();
    // Exit
    text.exit().remove();

    return Promise.all([axisTransition]);
  };

  private updateBars = (): Promise<void[]> => {
    const barHeight = this.y.bandwidth() * BAR_HEIGHT_RANGE_BAND_RATIO;

    // Select
    const bar = d3
      .select(this.selector)
      .selectAll<SVGGElement, Data>('.bar-group')
      .data(this.data, (d) => d.id);
    // Enter
    const barEnter = bar
      .enter()
      .append('g')
      .attr('class', 'bar-group')
      .attr(
        'transform',
        (d) => `translate(0,${(this.y(d.id) ?? 0) + this.y.bandwidth() / 2})`,
      );
    const barRect = barEnter
      .append('rect')
      .attr('class', 'bar-item')
      .attr('width', BAR_MIN_WIDTH)
      .attr('y', BAR_TEXT_PADDING / 2)
      .attr('height', barHeight);
    const barText = barEnter
      .append('text')
      .attr('class', 'bar-text')
      .attr('x', Y_AXIS_PADDING)
      .attr('y', -BAR_TEXT_PADDING / 2)
      .attr('fill', this.textColor);
    // Update
    const selection = bar
      .select<SVGTextElement>('text')
      .merge(barText)
      .attr('fill', this.textColor);
    if (
      shouldUpdateText({
        selection,
        texts: selection.data().map((d) => d.label),
        maxWidth: WIDTH,
        maxHeight: this.y.bandwidth() - barHeight,
      })
    ) {
      selection
        .text((d) => d.label)
        .call(wordWrap, {
          maxWidth: WIDTH,
          maxHeight: this.y.bandwidth() - barHeight,
          fontSize: NORMAL_FONT_SIZE,
          verticallyAlignMiddle: true,
        });
    }
    const transition = bar
      .merge(barEnter)
      .transition()
      .delay(ANIMATION_DELAY * this.animationMultiplier)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr(
        'transform',
        (d) => `translate(0,${(this.y(d.id) ?? 0) + this.y.bandwidth() / 2})`,
      )
      .end();
    const barTransition = bar
      .select<SVGRectElement>('.bar-item')
      .merge(barRect)
      .attr(
        'fill',
        (d) => this.fillColors[d.index % this.fillColors.length] ?? null,
      )
      .attr(
        'stroke',
        (d) => this.fillColors[d.index % this.fillColors.length] ?? null,
      )
      .transition()
      .ease(d3.easeBack)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr(
        'width',
        (d) => max([BAR_MIN_WIDTH, this.x(d.score)]) ?? BAR_MIN_WIDTH,
      )
      .attr('height', barHeight)
      .end();
    // Exit
    bar.exit().remove();

    return Promise.all([transition, barTransition]);
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns array of methods that return promises (animations)
   */
  public update = (): Promise<void[][]> => {
    const rangeOptions = rangeBandOptions(HEIGHT, this.data.length);
    this.x
      .domain([0, max(this.data.map((d) => d.score)) || 1])
      .range([0, WIDTH]);
    this.y
      .domain(this.data.map((d) => d.id))
      .rangeRound(rangeOptions.r)
      .padding(rangeOptions.p);

    const axisTransition = this.updateAxis();
    const barsTransition = this.updateBars();
    this.updateLine();

    return Promise.all([axisTransition, barsTransition]);
  };
}
